import React from 'react';

/**
 * BudgetComparisonTable
 * Renders a Actual vs Budget vs Variance table below the physical-asset expense chart.
 * Props:
 *   tableData: { rows, actTotal, budTotal, periodLabel } | null
 *   title: string (optional)
 */
const BudgetComparisonTable = ({ tableData, title = 'Budget vs Actual' }) => {
  if (!tableData || !tableData.rows || tableData.rows.length === 0) return null;

  const { rows, actTotal, budTotal, periodLabel } = tableData;
  const totalVariance = actTotal - budTotal;

  const fmt = (v) =>
    `$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const varColor = (a, b) => (a > b ? '#f87171' : '#34d399');
  const varSign  = (v) => (v > 0 ? '+' : v < 0 ? '-' : '');

  const colStyle = { textAlign: 'right', paddingRight: 10, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 };
  const labelColStyle = { textAlign: 'left', paddingLeft: 6, paddingRight: 8, fontSize: 12, whiteSpace: 'nowrap', fontFamily: 'monospace' };
  const thStyle = { color: '#9ca3af', fontWeight: 600, paddingBottom: 4, paddingRight: 10, textAlign: 'right', fontSize: 11, borderBottom: '1px solid #374151' };
  const thLabelStyle = { color: '#9ca3af', fontWeight: 600, paddingBottom: 4, paddingLeft: 6, textAlign: 'left', fontSize: 11, borderBottom: '1px solid #374151' };

  return (
    <div style={{ background: '#0b1220', border: '1px solid #374151', borderRadius: 8, padding: '10px 14px', marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ color: '#e5e7eb', fontWeight: 600, fontSize: 13 }}>{title}</span>
        {periodLabel && (
          <span style={{ color: '#6b7280', fontSize: 11 }}>Period: {periodLabel}</span>
        )}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thLabelStyle}>Category</th>
            <th style={thStyle}>Actual</th>
            <th style={thStyle}>Budget</th>
            <th style={thStyle}>Variance</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const variance = row.a - row.b;
            const isParent = row.isParent;
            const indent   = row.indent;
            const isUnmapped = row.isUnmapped;
            return (
              <tr key={idx} style={{ borderBottom: '1px solid #1f2937', background: isUnmapped ? '#1c1408' : undefined }}>
                <td style={{
                  ...labelColStyle,
                  paddingLeft: indent ? 20 : 6,
                  color: isUnmapped ? '#fbbf24' : isParent ? '#e5e7eb' : '#9ca3af',
                  fontWeight: isParent || isUnmapped ? 700 : 400,
                  paddingTop: isParent ? 5 : 2,
                  paddingBottom: isParent ? 5 : 2,
                  fontStyle: isUnmapped ? 'italic' : undefined,
                }}>
                  {indent ? '↳ ' : ''}{row.label}
                </td>
                <td style={{ ...colStyle, color: isUnmapped ? '#fbbf24' : row.a > 0 ? '#e5e7eb' : '#4b5563' }}>
                  {row.a > 0 ? fmt(row.a) : '—'}
                </td>
                <td style={{ ...colStyle, color: row.b > 0 ? '#34d399' : '#4b5563' }}>
                  {row.b > 0 ? fmt(row.b) : '—'}
                </td>
                <td style={{ ...colStyle, color: isUnmapped ? '#fbbf24' : row.a === 0 && row.b === 0 ? '#4b5563' : varColor(row.a, row.b) }}>
                  {row.a === 0 && row.b === 0 ? '—' : `${varSign(variance)}${fmt(variance)}`}
                </td>
              </tr>
            );
          })}
          {/* Totals row */}
          <tr style={{ borderTop: '2px solid #374151' }}>
            <td style={{ ...labelColStyle, color: '#e5e7eb', fontWeight: 700, paddingTop: 6 }}>TOTAL</td>
            <td style={{ ...colStyle, color: '#e5e7eb', fontWeight: 700, paddingTop: 6 }}>{fmt(actTotal)}</td>
            <td style={{ ...colStyle, color: '#34d399', fontWeight: 700, paddingTop: 6 }}>{fmt(budTotal)}</td>
            <td style={{ ...colStyle, color: varColor(actTotal, budTotal), fontWeight: 700, paddingTop: 6 }}>
              {varSign(totalVariance)}{fmt(totalVariance)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default BudgetComparisonTable;
